#include <packing>

uniform sampler2D uColorTexture;
uniform sampler2D uPointDepthTexture;
uniform sampler2D uTerrainColorTexture;
uniform sampler2D uTerrainDepthTexture;

uniform vec2 uResolution;

uniform float uCameraNear;
uniform float uCameraFar;
uniform float uTerrainFade;
uniform float uOccludedOpacity;
uniform bool uIsOrtho;
uniform float uFrustumWidth;

in vec2 vUv;

out vec4 FragColor;

const float EPSILON = 1e-6;
const float EDL_STRENGTH = 24.0;

// Decode a depth-texture sample to linear orthographic depth (0 = near, 1 = far).
// Points and terrain share the same log-depth encoding, so this works for both.
float linearize(float fragCoordZ) {
    if (uIsOrtho) {
        return fragCoordZ;
    }

    float viewZ = 1.0 - pow(uCameraFar + 1.0, fragCoordZ);
    return viewZToOrthographicDepth(viewZ, uCameraNear, uCameraFar);
}

// Soft geometric occlusion (1 where a point sits behind the terrain)
float terrainOcclusion(vec2 coord) {
    float terrainZ = texture(uTerrainDepthTexture, coord).x;
    float transition = max(EPSILON, fwidth(terrainZ));
    if (uTerrainFade <= EPSILON || texture(uTerrainColorTexture, coord).a <= EPSILON) return 0.0;
    if (terrainZ >= 1.0 - EPSILON) return 0.0;

    float pointZ = texture(uPointDepthTexture, coord).x;
    return smoothstep(0.0, transition, pointZ - terrainZ);
}

float getPointDepth(vec2 coord) {
    if (texture(uColorTexture, coord).a == 0.0) {
        return 1.0;
    }
    
    return linearize(texture(uPointDepthTexture, coord).x);
}

float getTerrainDepth(vec2 coord) {
    if (texture(uTerrainColorTexture, coord).a == 0.0) {
        return 1.0;
    }
    
    return linearize(texture(uTerrainDepthTexture, coord).x);
}

// Only neighbouring points in front contribute to EDL
float edlEdge(float centerDepth, vec2 coord) {
    float terrainDepth = getTerrainDepth(coord);
    float pointDepth = getPointDepth(coord);
    
    if (terrainDepth < pointDepth) {
        return 0.0;
    }

    return max(0.0, centerDepth - pointDepth);
}

void main() {
    vec4 outputColor = texture(uColorTexture, vUv);
    float occlusion = terrainOcclusion(vUv);
    outputColor.a *= mix(1.0, uOccludedOpacity, occlusion * uTerrainFade);

    float terrainDepth = getTerrainDepth(vUv);
    float pointDepth = getPointDepth(vUv);
    float depth_raw = min(pointDepth, terrainDepth);

    float dX = 1.0 / uResolution[0];
    float dY = 1.0 / uResolution[1];

    float edl =
        edlEdge(depth_raw, vUv + vec2(dX, 0.0)) +
        edlEdge(depth_raw, vUv + vec2(-dX, 0.0)) +
        edlEdge(depth_raw, vUv + vec2(0.0, dY)) +
        edlEdge(depth_raw, vUv + vec2(0.0, -dY));

    if (uIsOrtho) {
        float frustumDepth = uCameraFar - uCameraNear;
        edl *= frustumDepth / max(uFrustumWidth, EPSILON);
    } else {
        edl /= max(depth_raw, EPSILON);
    }

    float shade = clamp(edl * EDL_STRENGTH, 0.0, 0.80);

    if (shade > 0.0) {
        // Composite opaque black at `shade` over the (straight-alpha) surface. For a
        // point-less pixel this is black at alpha = shade, darkening the terrain beneath.
        float alpha = outputColor.a + shade * (1.0 - outputColor.a);
        FragColor = vec4(outputColor.rgb * outputColor.a * (1.0 - shade) / alpha, alpha);
    } else if (outputColor.a == 0.0) {
        discard;
    } else {
        FragColor = outputColor;
    }
}
